import React, { useState, useRef } from "react";
import moment from "moment";
import axios from "axios";
import {
  Table,
  Button,
  Tooltip,
  Modal,
  message,
  Input,
  Space,
  Select,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import VerificationStatusTag from "../shared/VerificationStatusTag";
import UserBadgeTag from "../shared/UserBadgeTag";
import UserStatusTag from "../shared/UserStatusTag";
import TopupPackageTag from "../shared/TopupPackageTag";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";
import { formatDateTime, getRelativeTimeFromNow } from "../../helpers/date";
const UserList = ({
  users,
  selectedRows,
  onSelectRows,
  loading,
  accountManager,
}) => {
  const { permissions } = usePermissions();
  const tableColumns = [
    {
      title: "User Id",
      dataIndex: "userId",
      key: "userId",
      width: 120,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 250,
      sorter: {
        compare: (a, b) => a.username.length - b.username.length,
        multiple: 1,
      },
      sortDirections: ["descend", "ascend"],
      render: (text, row) => <a href={`/user/${row.userId}`}>{text}</a>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 250,
    },
    {
      title: "Phone",
      dataIndex: ["countryCode", "phone"],
      key: "phone",
      width: 150,
      render: (text, row) =>
        row.countryCode && row.phone
          ? `${row.countryCode}${row.phone}`
          : row.phone
            ? row.phone
            : "-",
    },
    {
      title: "Base State",
      dataIndex: ["baseState"],
      key: "baseState",
      width: 80,
    },
    {
      title: "Credit",
      dataIndex: ["merchant", "credits"],
      key: "credit",
      width: 100,
      sorter: {
        compare: (a, b) => a.merchant.credits - b.merchant.credits,
        multiple: 2,
      },
      render: (text, row) => text?.toFixed(2),
    },
    {
      title: "Total Topup",
      dataIndex: ["merchant", "totalTopup"],
      key: "totalTopup",
      sorter: {
        compare: (a, b) => a.totalTopup - b.totalTopup,
        multiple: 3,
      },
      width: 100,
      render: (text, row) => text,
    },
    {
      title: "Package",
      dataIndex: ["merchant", "topupPackage"],
      key: "topupPackage",
      width: 100,
      render: (text) => (text ? <TopupPackageTag packageKey={text} /> : "-"),
    },
    {
      title: "Badge",
      dataIndex: "badge",
      key: "badge",
      width: 100,
      render: (text, row) =>
        text ? <UserBadgeTag statusKey={text}>Verified</UserBadgeTag> : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text, row) =>
        text ? <UserStatusTag statusKey={text}>Verified</UserStatusTag> : "-",
    },
    {
      title: "Total Usage",
      dataIndex: "totalUsage",
      key: "totalUsage",
      render: (text) => (text ? text.toFixed(2) : "-"),
      sorter: {
        compare: (a, b) => a.totalUsage - b.totalUsage,
        multiple: 2,
      },
      width: 100,
    },
    {
      title: "Total Order",
      dataIndex: "totalNoOrder",
      key: "totalNoOrder",
      sorter: {
        compare: (a, b) => a.totalTopup - b.totalTopup,
        multiple: 3,
      },
      width: 100,
    },
    {
      title: "Last Order Date",
      dataIndex: "lastOrderAt",
      key: "lastOrderAt",
      width: 100,
      render: (text) => (text === null || text === undefined ? "-" : <Tooltip placement="topLeft" title={formatDateTime(text)}>
        {getRelativeTimeFromNow(text)}
      </Tooltip>),
    },
    {
      title: "Last Topup Date",
      dataIndex: "lastTopupAt",
      key: "lastTopupAt",
      width: 120,
      render: (text) => (text === null || text === undefined ? "-" : <Tooltip placement="topLeft" title={formatDateTime(text)}>
        {getRelativeTimeFromNow(text)}
      </Tooltip>),
    },
    {
      title: "Last Active Date",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      width:  120,
      render: (text) => (text === null || text === undefined ? "-" : <Tooltip placement="topLeft" title={formatDateTime(text)}>
        {getRelativeTimeFromNow(text)}
      </Tooltip>),
    },
    {
      title: "BRN",
      dataIndex: ["merchant", "BRN"],
      key: "brn",
      width: 150,
    },
    {
      title: "Referral Code",
      dataIndex: "referralCode",
      key: "referralCode",
      render: (text) => (text ? text : "-"),
      sorter: (a, b) => {
        a = a.referralCode || "";
        b = b.referralCode || "";
        return a.localeCompare(b);
      },
      width: 150,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Locked Tier",
      dataIndex: "lockedTier",
      key: "lockedTier",
      width: 120,
      render: (text, row) => (text ? "True" : "False"),
    },
    {
      title: "Email Verification",
      dataIndex: "emailVerifiedOn",
      render: (text, row) => {
        const isEmailVerified =
          text !== null && text !== "" && text !== undefined;

        if (permissions.includes(PERMISSIONS.VERIFY_EMAIL)) {
          return isEmailVerified ? (
            <VerificationStatusTag
              statusKey="Verified"
              title={new Date(text).toLocaleString()}
            >
              Verified {text !== ""}
            </VerificationStatusTag>
          ) : (
            <React.Fragment>
              <Tooltip placement="topLeft" title="Verify User Email">
                <Button
                  onClick={() => verifyHandler(row)}
                  type="primary"
                  size="small"
                  aria-label="Verify Email"
                >
                  Verify
                </Button>
              </Tooltip>
            </React.Fragment>
          );
        }

        return (
          <VerificationStatusTag
            statusKey={isEmailVerified ? "Verified" : "Unverified"}
            title={isEmailVerified ? new Date(text).toLocaleString() : ""}
          />
        );
      },
      width: 150,
    },
    {
      title: "Phone Verification",
      dataIndex: "phoneVerifiedOn",
      width: 150,
      render: (text, row) => (
        <VerificationStatusTag
          statusKey={text ? "Verified" : "Unverified"}
          title={text ? new Date(text).toLocaleString() : ""}
        />
      ),
    },
    {
      title: "Account Manager",
      dataIndex: ["manager", "email"],
      render: (text, row) => {
        return permissions?.includes(PERMISSIONS.ASSIGN_ACCOUNT_MANAGER) ? (
          <div>
            <Select
              value={text || "Select"}
              className="select-before"
              // style={{ width: "100px" }} // Adjusted width using style prop
              onChange={(value) => addAccountManager(row, value)}
            >
              {accountManager.map((manager) => (
                <Select.Option key={manager.email} value={manager.email}>
                  {manager.email}
                </Select.Option>
              ))}
            </Select>
          </div>
        ) : (
          text || "-"
        );
      },
      width: 150,
    },
    {
      title: "Credit Tagging",
      dataIndex: "tagging",
      width: 160,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_CREDIT_TAGGING) ? (
          <div>
            <Select
              value={text ? text : "Select"}
              className="select-before"
              width="250px"
              onChange={(value) => updateUserTagging(row, value)}
            >
              <Select.Option value="Topup User">Topup User</Select.Option>
              <Select.Option value="Credit User">Credit User</Select.Option>
            </Select>
          </div>
        ) : (
          text || "-"
        );
      },
    },
    {
      title: "Suspend User",
      dataIndex: "suspendedAt",
      render: (text, row) => {

        const isSuspend = row.suspendedAt !== null
        return permissions.includes(PERMISSIONS.VERIFY_EMAIL) ? (
          <div>
            <Select
              defaultValue={text !==null ? isSuspend : "Select"}
              className="select-before"
              width="250px"
              onChange={(value) => updateUserSuspendStatus(row, value)}
            >
              <Select.Option value={true}>Suspend</Select.Option>
              <Select.Option value={false}>Unsuspend</Select.Option>
            </Select>
          </div>
        ) : (
          text || "-"
        );
      },
      width: 150,
    },
    {
      title: "Suspended At",
      dataIndex: "suspendedAt",
      width: 150,
      render: (text, row) => (text ? new Date(text).toLocaleString() : "-"),
    },
    {
      title: "Sub Account",
      dataIndex: ["isSubAccount"],
      key: "isSubAccount",
      width: 80,
      render: (text, row) => text === true ? "Y" : "N",
    },
    {
      title: "Master Email",
      dataIndex: ["master","email"],
      width: 150,
      render: (text, row) => (text ? text : "-"),
    },
  ];

  const addAccountManager = async (row, value) => {
    const addManager = await axios
      .patch("/api/user/add-manager", {
        userId: row.userId,
        managerEmail: value,
      })
      .then((res) => {
        message.info(`${row.email} is succesfully assign to ${value} `);
      })
      .catch((error) => {
        message.error(error.message);
      });
  };

  const updateUserTagging = async (row, value) => {
    const updateTagging = await axios
      .patch("/api/user/update-tagging", {
        userId: row.userId,
        tagging: value,
      })
      .then((res) => {
        message.info(`${row.email} is succesfully assign as ${value} `);
      })
      .catch((error) => {
        message.error(error.message);
      });
  };

  const updateUserSuspendStatus = async (row, value) => {
    const updateTagging = await axios
      .patch("/api/user/update-suspend-status", {
        userId: row.userId,
        suspended: value,
      })
      .then((res) => {
        message.info(
          `${row.email} is succesfully ${value ? "suspended" : "unsuspended"} `
        );
      })
      .catch((error) => {
        message.error(error.message);
      });
  };

  const verifyHandler = (user) => {
    const messageContent = (
      <p>
        Email: {user.email}
        <br />
      </p>
    );

    Modal.confirm({
      content: messageContent,
      title: `Verify this user's email?`,
      onOk() {
        axios
          .patch(`/api/users/${user.userId}/manual-confirm`)
          .then((res) => {
            message.info(`Email verification for (${user.email}) is success.`);
          })
          .catch((error) => {
            message.error(error.message);
          });
      },
    });
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            this.searchInput = node;
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            this.handleSearch(selectedKeys, confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => this.handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              this.setState({
                searchText: selectedKeys[0],
                searchedColumn: dataIndex,
              });
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => this.searchInput.select(), 100);
      }
    },
    render: (text) => (this.state.searchedColumn === dataIndex ? text : text),
  });

  return (
    <div className="table">
      <Table
        rowKey="id"
        rowSelection={{ selectedRows, onChange: onSelectRows }}
        className="table-section"
        columns={tableColumns}
        dataSource={users}
        pagination={false}
        scroll={{ x: 2000, y: 600 }}
        loading={loading}
      />
    </div>
  );
};

export default UserList;
